/**
 * useRevealAnimation - Shared animation logic for AI Sections components
 *
 * Handles staged reveal animation, shimmer effect timing, and CSS variable
 * detection for sparkle color. Used by AISectionsButton and AISectionsSearchBar.
 */

import { type RefObject, useEffect, useState } from 'react';

interface UseRevealAnimationOptions {
    /** Whether to show the element (triggers entrance animation) */
    show: boolean;
    /** Delay before showing (for staggered animation) in ms */
    animationDelay: number;
    /** Whether to show shimmer effect on first reveal */
    showShimmer: boolean;
    /** Ref to the container element for reading CSS variables */
    containerRef: RefObject<HTMLElement>;
}

interface UseRevealAnimationResult {
    /** Whether the element has completed its reveal animation */
    isRevealed: boolean;
    /** Whether the shimmer effect should be visible */
    showShimmerEffect: boolean;
    /** CSS variable value for sparkle color (if set via customCSS) */
    cssSparkleColor: string | null;
}

/**
 * Hook for managing reveal animations with optional shimmer effect
 *
 * @example
 * ```tsx
 * const { isRevealed, showShimmerEffect, cssSparkleColor } = useRevealAnimation({
 *     show: true,
 *     animationDelay: 100,
 *     showShimmer: true,
 *     containerRef,
 * });
 * ```
 */
export function useRevealAnimation({
    show,
    animationDelay,
    showShimmer,
    containerRef,
}: UseRevealAnimationOptions): UseRevealAnimationResult {
    const [isRevealed, setIsRevealed] = useState(false);
    const [hasShownOnce, setHasShownOnce] = useState(false);
    const [showShimmerEffect, setShowShimmerEffect] = useState(false);
    const [cssSparkleColor, setCssSparkleColor] = useState<string | null>(null);

    // Track first reveal
    useEffect(() => {
        if (show && !hasShownOnce) {
            setHasShownOnce(true);
        }
    }, [show, hasShownOnce]);

    // Staged reveal with animation delay
    useEffect(() => {
        if (show) {
            let rafId: number | undefined;
            const timeoutId = setTimeout(() => {
                rafId = requestAnimationFrame(() => {
                    setIsRevealed(true);
                });
            }, animationDelay);
            return () => {
                clearTimeout(timeoutId);
                if (rafId !== undefined) {
                    cancelAnimationFrame(rafId);
                }
            };
        }
        setIsRevealed(false);
    }, [show, animationDelay]);

    // Trigger shimmer effect after first reveal
    useEffect(() => {
        if (hasShownOnce && showShimmer && isRevealed) {
            let innerTimer: ReturnType<typeof setTimeout> | undefined;
            const timer = setTimeout(() => {
                setShowShimmerEffect(true);
                innerTimer = setTimeout(() => setShowShimmerEffect(false), 800);
            }, 300 + animationDelay);
            return () => {
                clearTimeout(timer);
                if (innerTimer) {
                    clearTimeout(innerTimer);
                }
            };
        } else {
            // Reset shimmer when conditions are no longer met
            setShowShimmerEffect(false);
        }
    }, [hasShownOnce, showShimmer, isRevealed, animationDelay]);

    // Read CSS variable for sparkle color (allows customCSS to override primaryColor)
    // Only runs once when revealed to avoid repeated getComputedStyle calls
    useEffect(() => {
        if (containerRef.current && isRevealed && !cssSparkleColor) {
            const computed = getComputedStyle(containerRef.current);
            const cssColor = computed.getPropertyValue('--ai-sections-sparkle-color').trim();
            if (cssColor) {
                setCssSparkleColor(cssColor);
            }
        }
    }, [isRevealed, containerRef, cssSparkleColor]);

    return {
        isRevealed,
        showShimmerEffect,
        cssSparkleColor,
    };
}
